import type {
  CursorPaginatedResponse,
  Customer,
  CustomerListParams,
  CustomerPayload,
} from '@/libs/interfaces'

import { MOCK_ORG_ID, mockCustomers } from '../fixtures/seed'
import { delay, genID, matchesSearch, nowISO, paginate } from '../helpers'
import { createEntityStore } from '../store'

const customerStore = createEntityStore<Customer, 'driverID'>(mockCustomers, 'driverID')

const filterCustomers = (params: CustomerListParams): Customer[] =>
  customerStore
    .getAll()
    .filter((customer) =>
      matchesSearch(customer, params.search, [
        'driverFirstName',
        'driverLastName',
        'driverEmail',
        'driverPhoneNumber',
      ]),
    )

export const customersApiMock = {
  create: async (payload: CustomerPayload): Promise<Customer> => {
    const customer: Customer = {
      driverID: genID('customer'),
      driverFirstName: '',
      driverLastName: '',
      orgID: MOCK_ORG_ID,
      driverStatus: 'Active',
      createdAt: nowISO(),
      updatedAt: nowISO(),
      ...payload.driver,
    }
    customerStore.insert(customer)
    return delay(customer)
  },
  delete: async (payload: Partial<Customer>) => {
    const { driverID = '' } = payload
    if (driverID) customerStore.remove(driverID)
    return delay(undefined)
  },
  getByID: async (params: { orgID?: string; driverID?: string }): Promise<Partial<Customer>> => {
    const customer = customerStore.getById(params.driverID ?? '')
    return delay(customer ?? {})
  },
  list: async (params: CustomerListParams): Promise<Customer[]> => delay(filterCustomers(params)),
  listCursor: async (params: CustomerListParams): Promise<CursorPaginatedResponse<Customer>> =>
    delay(paginate(filterCustomers(params), params)),
  update: async (payload: Partial<Customer>) => {
    const updated = payload.driverID
      ? customerStore.update(payload.driverID, {
          ...payload,
          updatedAt: nowISO(),
        })
      : undefined
    return delay(updated ?? payload)
  },
}
